// Plik: frontend/src/pages/admin/SiteSettings.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Typography, Box, Paper, Grid, Button,
  TextField, Divider, Alert, CircularProgress,
  Card, CardContent, CardActions, Chip,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import DescriptionIcon from '@mui/icons-material/Description';
import { settingsService } from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import toast from 'react-hot-toast';

const SiteSettings = () => {
  const fileInputRef = useRef(null);
  
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const fetchSettings = async () => {
    setLoading(true);
    const response = await settingsService.getSettings();
    if (response.success) {
      setSettings(response.data);
    } else {
      setError(response.error);
      toast.error('Nie udało się pobrać ustawień');
    }
    setLoading(false);
  };
  
  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };
  
  const handleSave = async () => {
    setSaving(true);
    const response = await settingsService.updateSettings(settings);
    if (response.success) {
      toast.success('Ustawienia zapisane pomyślnie');
    } else {
      toast.error(response.error);
    }
    setSaving(false);
  };
  
  const handleUploadRegulamin = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const response = await settingsService.uploadRegulamin(file);
    if (response.success) {
      toast.success('Regulamin przesłany pomyślnie');
      setSettings(prev => ({ ...prev, regulamin_url: response.data.url }));
    } else {
      toast.error(response.error);
    }
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleDeleteRegulamin = async () => {
    if (!window.confirm('Czy na pewno chcesz usunąć regulamin?')) return;
    
    const response = await settingsService.deleteRegulamin();
    if (response.success) {
      toast.success('Regulamin usunięty');
      setSettings(prev => ({ ...prev, regulamin_url: '' }));
    } else {
      toast.error(response.error);
    }
  };
  
  if (loading) {
    return (
      <AdminLayout title="Ustawienia Strony">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout title="Ustawienia Strony">
      {/* Header with save */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Ustawienia Strony
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Edytuj treści wyświetlane na publicznych stronach systemu rejestracji.
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{ minWidth: 180 }}
        >
          {saving ? 'Zapisywanie...' : 'Zapisz ustawienia'}
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}
      
      {/* Regulamin - Upload */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          📄 Regulamin wydarzenia
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {settings.regulamin_url ? (
                <Card variant="outlined" sx={{ bgcolor: '#f8fdf8' }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1 }}>
                    <DescriptionIcon color="success" fontSize="large" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Regulamin załadowany
                      </Typography>
                      <Chip label="Aktywny" color="success" size="small" />
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      color="primary"
                      href={settings.regulamin_url}
                      target="_blank"
                    >
                      Podgląd
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={handleDeleteRegulamin}
                      startIcon={<DeleteIcon />}
                    >
                      Usuń
                    </Button>
                  </CardActions>
                </Card>
              ) : (
                <Alert severity="info" variant="outlined">
                  Brak przesłanego regulaminu. Link do regulaminu nie będzie widoczny na stronie rejestracji.
                </Alert>
              )}
              
              <Box>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleUploadRegulamin}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                >
                  {uploading ? 'Przesyłanie...' : (settings.regulamin_url ? 'Podmień regulamin' : 'Prześlij regulamin')}
                </Button>
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                  Dozwolone formaty: PDF, DOC, DOCX. Maks. 10MB
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Tekst linka regulaminu w formularzu"
              fullWidth
              value={settings.terms_label || ''}
              onChange={(e) => handleChange('terms_label', e.target.value)}
              variant="outlined"
              helperText="Tekst wyświetlany przy checkboxie akceptacji regulaminu"
            />
          </Grid>
        </Grid>
      </Paper>
      
      {/* Strona główna */}
      <Accordion defaultExpanded sx={{ mb: 2, '&:before': { display: 'none' }, borderRadius: '8px !important', border: '1px solid', borderColor: 'divider' }} elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight={600}>🏠 Strona główna</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Tytuł strony"
                fullWidth
                value={settings.home_title || ''}
                onChange={(e) => handleChange('home_title', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Opis główny"
                fullWidth
                multiline
                rows={3}
                value={settings.home_description || ''}
                onChange={(e) => handleChange('home_description', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Tekst zachęty (CTA)"
                fullWidth
                value={settings.home_cta_text || ''}
                onChange={(e) => handleChange('home_cta_text', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Tekst przycisku"
                fullWidth
                value={settings.home_button_text || ''}
                onChange={(e) => handleChange('home_button_text', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Tekst stopki"
                fullWidth
                value={settings.home_footer_text || ''}
                onChange={(e) => handleChange('home_footer_text', e.target.value)}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* Formularz rejestracji */}
      <Accordion sx={{ mb: 2, '&:before': { display: 'none' }, borderRadius: '8px !important', border: '1px solid', borderColor: 'divider' }} elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight={600}>📝 Formularz rejestracji</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Tytuł formularza"
                fullWidth
                value={settings.registration_title || ''}
                onChange={(e) => handleChange('registration_title', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Tekst zgody na kontakt"
                fullWidth
                value={settings.contact_consent_label || ''}
                onChange={(e) => handleChange('contact_consent_label', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Tekst zgody marketingowej"
                fullWidth
                value={settings.marketing_consent_label || ''}
                onChange={(e) => handleChange('marketing_consent_label', e.target.value)}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* Strona podziękowania */}
      <Accordion sx={{ mb: 2, '&:before': { display: 'none' }, borderRadius: '8px !important', border: '1px solid', borderColor: 'divider' }} elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight={600}>🎉 Strona podziękowania (Thank You)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Tytuł"
                fullWidth
                value={settings.thankyou_title || ''}
                onChange={(e) => handleChange('thankyou_title', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Podtytuł"
                fullWidth
                value={settings.thankyou_subtitle || ''}
                onChange={(e) => handleChange('thankyou_subtitle', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Wiadomość 1 (email)"
                fullWidth
                multiline
                rows={2}
                value={settings.thankyou_message_1 || ''}
                onChange={(e) => handleChange('thankyou_message_1', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Wiadomość 2 (sprawdź skrzynkę)"
                fullWidth
                multiline
                rows={2}
                value={settings.thankyou_message_2 || ''}
                onChange={(e) => handleChange('thankyou_message_2', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Wiadomość 3 (kontakt)"
                fullWidth
                multiline
                rows={2}
                value={settings.thankyou_message_3 || ''}
                onChange={(e) => handleChange('thankyou_message_3', e.target.value)}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* Przycisk zapisu na dole */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {saving ? 'Zapisywanie...' : 'Zapisz wszystkie ustawienia'}
        </Button>
      </Box>
    </AdminLayout>
  );
};

export default SiteSettings;
